/**
 * ZanzibarMap Component
 * Interactive choropleth map with school markers for Zanzibar education data
 *
 * Features:
 * - Colored region/district/ward boundaries by enrollment, school count, or gender parity
 * - School dot markers from GPS data (~1,576 schools)
 * - Drill-down: click region → districts, click district → wards + schools
 * - Tooltips on hover, popups on click
 * - Color legend, tile layer switcher, breadcrumb navigation
 */

import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import {
  MapContainer,
  TileLayer,
  GeoJSON,
  CircleMarker,
  Popup,
  Tooltip,
  useMap,
} from 'react-leaflet';
import L from 'leaflet';
import {
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  CircularProgress,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  Paper,
  IconButton,
  Alert,
} from '@mui/material';
import MapIcon from '@mui/icons-material/Map';
import SatelliteIcon from '@mui/icons-material/Satellite';
import TerrainIcon from '@mui/icons-material/Terrain';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import SchoolIcon from '@mui/icons-material/School';
import LayersIcon from '@mui/icons-material/Layers';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

import { useMapData, MapLevel } from './useMapData';
import MapLegend from './MapLegend';
import MapSidebar from './MapSidebar';
import {
  ZANZIBAR_CENTER,
  DEFAULT_ZOOM,
  REGION_ZOOM,
  DISTRICT_ZOOM,
  WARD_ZOOM,
  TILE_LAYERS,
  getFeatureStyle,
  getFeatureValue,
  getFeatureName,
  getSchoolMarkerColor,
  formatNumber,
  IndicatorType,
  GeoFeatureData,
} from './mapUtils';

import 'leaflet/dist/leaflet.css';
import './Map.css';

// Fix Leaflet default marker icon issue with webpack
// We use CircleMarkers instead of default icons, but fix anyway for any popups
// @ts-ignore
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// === Map View Controller (child component to access map instance) ===
interface MapViewControllerProps {
  center: [number, number];
  zoom: number;
  bounds?: L.LatLngBoundsExpression;
}

const MapViewController: React.FC<MapViewControllerProps> = ({ center, zoom, bounds }) => {
  const map = useMap();

  useEffect(() => {
    if (bounds) {
      map.fitBounds(bounds as L.LatLngBoundsExpression, { padding: [30, 30], maxZoom: 14 });
    } else {
      map.setView(center, zoom, { animate: true });
    }
  }, [map, center, zoom, bounds]);

  return null;
};

// === Main Component ===
interface ZanzibarMapProps {
  height?: string | number;
}

const ZanzibarMap: React.FC<ZanzibarMapProps> = ({ height = '100%' }) => {
  // Navigation state
  const [level, setLevel] = useState<MapLevel>('region');
  const [selectedRegionCode, setSelectedRegionCode] = useState<string | undefined>();
  const [selectedRegionName, setSelectedRegionName] = useState<string>('');
  const [selectedDistrictCode, setSelectedDistrictCode] = useState<string | undefined>();
  const [selectedDistrictName, setSelectedDistrictName] = useState<string>('');

  // Map state
  const [indicator, setIndicator] = useState<IndicatorType>('enrollment');
  const [tileLayer, setTileLayer] = useState<'standard' | 'satellite' | 'terrain'>('standard');
  const [showSchools, setShowSchools] = useState(false);
  const [hoveredFeature, setHoveredFeature] = useState<string | null>(null);

  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarData, setSidebarData] = useState<GeoFeatureData | null>(null);
  const [sidebarAreaName, setSidebarAreaName] = useState('');
  const [sidebarChildAreas, setSidebarChildAreas] = useState<{ name: string; value: number }[]>([]);

  // Map bounds for fit
  const [mapBounds, setMapBounds] = useState<L.LatLngBoundsExpression | undefined>();

  // GeoJSON layer ref for bounds calculation
  const geoJsonRef = useRef<L.GeoJSON | null>(null);

  // Fetch map data
  const {
    geoJsonData,
    loading,
    error,
    dataMap,
    breaks,
    colors,
    schoolsData,
    schoolsLoading,
    refetch,
  } = useMapData({
    level,
    indicator,
    year: '2026',
    selectedRegionCode,
    selectedDistrictCode,
    showSchools,
  });

  // Calculate map center/zoom based on level
  const mapCenter = useMemo(() => ZANZIBAR_CENTER, []);
  const mapZoom = useMemo(() => {
    switch (level) {
      case 'district':
        return REGION_ZOOM;
      case 'ward':
        return DISTRICT_ZOOM;
      default:
        return DEFAULT_ZOOM;
    }
  }, [level]);

  // Style each GeoJSON feature
  const styleFeature = useCallback(
    (feature?: GeoJSON.Feature) => {
      if (!feature) return {};

      const value = getFeatureValue(feature, dataMap, indicator, level);
      const featureName = getFeatureName(feature, level);

      return getFeatureStyle({
        value,
        breaks,
        colors,
        isHovered: hoveredFeature === featureName,
        isSelected: false,
      });
    },
    [dataMap, indicator, level, breaks, colors, hoveredFeature]
  );

  // Handle feature hover
  const onEachFeature = useCallback(
    (feature: GeoJSON.Feature, layer: L.Layer) => {
      const featureName = getFeatureName(feature, level);
      const value = getFeatureValue(feature, dataMap, indicator, level);
      const props = feature.properties || {};

      // Build tooltip HTML
      const tooltipContent = `
        <div class="map-tooltip">
          <div class="tooltip-header">${featureName}</div>
          <div class="tooltip-row">
            <span class="tooltip-label">Students</span>
            <span class="tooltip-value">${formatNumber(dataMap.get(
              level === 'region' ? String(props.reg_code || '') :
              level === 'district' ? (props.dist_name || props.dist_code || '') :
              (props.ward_name || props.ward_code || '')
            )?.total_students)}</span>
          </div>
          <div class="tooltip-row">
            <span class="tooltip-label">Schools</span>
            <span class="tooltip-value">${formatNumber(dataMap.get(
              level === 'region' ? String(props.reg_code || '') :
              level === 'district' ? (props.dist_name || props.dist_code || '') :
              (props.ward_name || props.ward_code || '')
            )?.total_schools)}</span>
          </div>
          ${indicator === 'gender_parity' ? `
          <div class="tooltip-row">
            <span class="tooltip-label">GPI</span>
            <span class="tooltip-value">${(value || 0).toFixed(2)}</span>
          </div>
          ` : ''}
        </div>
      `;

      layer.bindTooltip(tooltipContent, {
        sticky: true,
        className: 'map-tooltip',
        direction: 'top',
        offset: [0, -10],
      });

      // Mouse events
      layer.on({
        mouseover: (e: L.LeafletMouseEvent) => {
          setHoveredFeature(featureName);
          const target = e.target;
          target.setStyle({
            weight: 3,
            color: '#333',
            dashArray: '',
            fillOpacity: 0.8,
          });
          target.bringToFront();
        },
        mouseout: (e: L.LeafletMouseEvent) => {
          setHoveredFeature(null);
          const target = e.target;
          target.setStyle(styleFeature(feature));
        },
        click: (e: L.LeafletMouseEvent) => {
          handleFeatureClick(feature, e);
        },
      });
    },
    [level, dataMap, indicator, styleFeature]
  );

  // Handle click on a feature - drill down or show info
  const handleFeatureClick = useCallback(
    (feature: GeoJSON.Feature, e: L.LeafletMouseEvent) => {
      const props = feature.properties || {};
      const featureName = getFeatureName(feature, level);

      // Get data for sidebar
      const matchKey = level === 'region' ? String(props.reg_code || '') :
                       level === 'district' ? (props.dist_name || props.dist_code || '') :
                       (props.ward_name || props.ward_code || '');
      const featureData = dataMap.get(matchKey) || null;

      // Open sidebar
      setSidebarAreaName(featureName);
      setSidebarData(featureData);
      setSidebarOpen(true);

      // Calculate bounds of clicked feature
      const layer = e.target;
      if (layer.getBounds) {
        setMapBounds(layer.getBounds());
      }

      // Drill down
      if (level === 'region') {
        const regCode = String(props.reg_code || '');
        setSelectedRegionCode(regCode);
        setSelectedRegionName(featureName);
        setLevel('district');
        setShowSchools(true);

        // Build child areas from district data
        // They'll be populated when district data loads
        setSidebarChildAreas([]);
      } else if (level === 'district') {
        // At district level: show sidebar info + zoom in, but DON'T drill to ward
        // This prevents the "vanishing map" issue where clicking a district hides
        // all other districts by switching to ward view.
        // Users can still see all districts and click different ones.
        // The sidebar shows detailed info about the clicked district.
        const distCode = String(props.dist_code || '');
        setSelectedDistrictCode(distCode);
        setSelectedDistrictName(featureName);
        // Don't drill to ward level — stay at district level
        // setLevel('ward');
      }
    },
    [level, dataMap]
  );

  // Drill down from district to ward level (triggered from sidebar button)
  const drillToWard = useCallback(() => {
    if (selectedDistrictCode && selectedRegionCode) {
      setLevel('ward');
    }
  }, [selectedDistrictCode, selectedRegionCode]);

  // Update child areas when data changes
  useEffect(() => {
    if (sidebarOpen && dataMap.size > 0) {
      const childAreas: { name: string; value: number }[] = [];
      dataMap.forEach((data, key) => {
        childAreas.push({
          name: key,
          value: data.total_students || 0,
        });
      });
      if (childAreas.length > 1) {
        setSidebarChildAreas(childAreas);
      }
    }
  }, [dataMap, sidebarOpen]);

  // Navigate back
  const navigateBack = useCallback(() => {
    if (level === 'ward') {
      setLevel('district');
      setSelectedDistrictCode(undefined);
      setSelectedDistrictName('');
      setMapBounds(undefined);
    } else if (level === 'district') {
      setLevel('region');
      setSelectedRegionCode(undefined);
      setSelectedRegionName('');
      setSelectedDistrictCode(undefined);
      setSelectedDistrictName('');
      setShowSchools(false);
      setMapBounds(undefined);
    }
    setSidebarOpen(false);
  }, [level]);

  // Navigate to specific level
  const navigateToLevel = useCallback((targetLevel: MapLevel) => {
    if (targetLevel === 'region') {
      setLevel('region');
      setSelectedRegionCode(undefined);
      setSelectedRegionName('');
      setSelectedDistrictCode(undefined);
      setSelectedDistrictName('');
      setShowSchools(false);
      setMapBounds(undefined);
      setSidebarOpen(false);
    } else if (targetLevel === 'district') {
      setLevel('district');
      setSelectedDistrictCode(undefined);
      setSelectedDistrictName('');
      setMapBounds(undefined);
    }
  }, []);

  // GeoJSON key for forced re-render
  // Only include selectedDistrictCode at ward level (where it affects filtering)
  // At district level, changing selectedDistrictCode shouldn't force GeoJSON re-render
  const geoJsonKey = useMemo(
    () => `${level}-${indicator}-${selectedRegionCode || ''}-${level === 'ward' ? (selectedDistrictCode || '') : ''}-${JSON.stringify(breaks)}`,
    [level, indicator, selectedRegionCode, selectedDistrictCode, breaks]
  );

  // Current tile config
  const currentTile = TILE_LAYERS[tileLayer];

  return (
    <Box sx={{ display: 'flex', height, position: 'relative' }}>
      {/* Main Map Area */}
      <Box sx={{ flex: 1, position: 'relative' }}>
        {/* Top Controls */}
        <Paper
          elevation={2}
          sx={{
            position: 'absolute',
            top: 10,
            left: 55,
            zIndex: 1000,
            p: 0.8,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
            backgroundColor: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(4px)',
          }}
        >
          {/* Back button */}
          {level !== 'region' && (
            <IconButton size="small" onClick={navigateBack} sx={{ color: '#1976D2' }}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          )}

          {/* Indicator selector */}
          <Select
            value={indicator}
            onChange={(e) => setIndicator(e.target.value as IndicatorType)}
            size="small"
            sx={{ fontSize: '0.75rem', height: 30, minWidth: 130 }}
          >
            <MenuItem value="enrollment">Enrollment</MenuItem>
            <MenuItem value="schools">Schools</MenuItem>
            <MenuItem value="gender_parity">Gender Parity</MenuItem>
          </Select>

          {/* School markers toggle */}
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showSchools}
                onChange={(e) => setShowSchools(e.target.checked)}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                <SchoolIcon sx={{ fontSize: 14 }} />
                <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                  Schools
                </Typography>
              </Box>
            }
            sx={{ mx: 0 }}
          />

          {/* Tile layer selector */}
          <ToggleButtonGroup
            value={tileLayer}
            exclusive
            onChange={(_, value) => value && setTileLayer(value)}
            size="small"
          >
            <ToggleButton value="standard" sx={{ py: 0.3, px: 0.8 }}>
              <MapIcon sx={{ fontSize: 16 }} />
            </ToggleButton>
            <ToggleButton value="satellite" sx={{ py: 0.3, px: 0.8 }}>
              <SatelliteIcon sx={{ fontSize: 16 }} />
            </ToggleButton>
            <ToggleButton value="terrain" sx={{ py: 0.3, px: 0.8 }}>
              <TerrainIcon sx={{ fontSize: 16 }} />
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Refresh */}
          <IconButton size="small" onClick={refetch} sx={{ color: '#666' }}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Paper>

        {/* Breadcrumb Navigation */}
        {level !== 'region' && (
          <Box className="map-breadcrumb">
            <Typography
              className="breadcrumb-item"
              onClick={() => navigateToLevel('region')}
              sx={{ cursor: 'pointer', color: '#1976D2', fontWeight: 500, fontSize: '0.75rem' }}
            >
              Zanzibar
            </Typography>
            <NavigateNextIcon sx={{ fontSize: 14, color: '#999' }} />
            {level === 'ward' ? (
              <>
                <Typography
                  className="breadcrumb-item"
                  onClick={() => navigateToLevel('district')}
                  sx={{ cursor: 'pointer', color: '#1976D2', fontWeight: 500, fontSize: '0.75rem' }}
                >
                  {selectedRegionName}
                </Typography>
                <NavigateNextIcon sx={{ fontSize: 14, color: '#999' }} />
                <Typography className="breadcrumb-current" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                  {selectedDistrictName}
                </Typography>
              </>
            ) : (
              <Typography className="breadcrumb-current" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                {selectedRegionName}
              </Typography>
            )}
          </Box>
        )}

        {/* Loading overlay */}
        {loading && (
          <Box className="map-loading-overlay">
            <CircularProgress size={40} sx={{ color: '#1976D2' }} />
            <Typography className="loading-text">Loading map data...</Typography>
          </Box>
        )}

        {/* Error */}
        {error && (
          <Alert
            severity="warning"
            sx={{ position: 'absolute', top: 60, left: 55, zIndex: 1000, maxWidth: 300 }}
            onClose={() => {}}
          >
            {error}
          </Alert>
        )}

        {/* The Map */}
        <MapContainer
          center={mapCenter}
          zoom={DEFAULT_ZOOM}
          style={{ width: '100%', height: '100%' }}
          zoomControl={true}
          scrollWheelZoom={true}
        >
          {/* Map view controller */}
          <MapViewController center={mapCenter} zoom={mapZoom} bounds={mapBounds} />

          {/* Tile layer */}
          <TileLayer url={currentTile.url} attribution={currentTile.attribution} />

          {/* GeoJSON choropleth layer */}
          {geoJsonData && breaks.length > 0 && (
            <GeoJSON
              key={geoJsonKey}
              data={geoJsonData}
              style={styleFeature}
              onEachFeature={onEachFeature}
              ref={(ref) => {
                if (ref) {
                  geoJsonRef.current = ref;
                }
              }}
            />
          )}

          {/* GeoJSON without data (gray boundaries) */}
          {geoJsonData && breaks.length === 0 && !loading && (
            <GeoJSON
              key={`${geoJsonKey}-nodata`}
              data={geoJsonData}
              style={() => ({
                fillColor: '#CCCCCC',
                weight: 1,
                opacity: 1,
                color: '#666',
                dashArray: '3',
                fillOpacity: 0.4,
              })}
              onEachFeature={(feature, layer) => {
                const name = getFeatureName(feature, level);
                layer.bindTooltip(name, { sticky: true, direction: 'top' });
                layer.on('click', (e) => handleFeatureClick(feature, e));
              }}
            />
          )}

          {/* School markers */}
          {showSchools &&
            schoolsData.map((school) => (
              <CircleMarker
                key={school.school_id}
                center={[school.latitude, school.longitude]}
                radius={5}
                pathOptions={{
                  fillColor: getSchoolMarkerColor(school.school_type),
                  color: '#fff',
                  weight: 1.5,
                  fillOpacity: 0.85,
                }}
              >
                <Popup className="school-popup" maxWidth={250}>
                  <div>
                    <div className="popup-header">{school.school_name}</div>
                    <div
                      className={`popup-type ${school.school_type?.toLowerCase()}`}
                    >
                      {school.school_type}
                    </div>
                    <div className="popup-detail">
                      <span>Registration:</span>
                      <strong>{school.school_reg_number}</strong>
                    </div>
                    <div className="popup-detail">
                      <span>Region:</span>
                      <strong>{school.region_name}</strong>
                    </div>
                    <div className="popup-detail">
                      <span>District:</span>
                      <strong>{school.district_name}</strong>
                    </div>
                    {school.gps_source && (
                      <div className="popup-detail">
                        <span>GPS Source:</span>
                        <strong>{school.gps_source}</strong>
                      </div>
                    )}
                  </div>
                </Popup>
                <Tooltip direction="top" offset={[0, -5]}>
                  {school.school_name}
                </Tooltip>
              </CircleMarker>
            ))}

          {/* School loading indicator */}
          {schoolsLoading && showSchools && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 40,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 1000,
                backgroundColor: 'rgba(255,255,255,0.9)',
                borderRadius: 2,
                px: 2,
                py: 0.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <CircularProgress size={16} />
              <Typography variant="caption">Loading schools...</Typography>
            </Box>
          )}
        </MapContainer>

        {/* Legend */}
        <MapLegend breaks={breaks} colors={colors} indicator={indicator} />

        {/* School type legend (when schools visible) */}
        {showSchools && schoolsData.length > 0 && (
          <Paper
            elevation={2}
            sx={{
              position: 'absolute',
              bottom: 30,
              left: 10,
              zIndex: 1000,
              p: 1,
              borderRadius: 2,
              backgroundColor: 'rgba(255,255,255,0.93)',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.3,
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem', mb: 0.3 }}>
              Schools ({schoolsData.length})
            </Typography>
            {[
              { color: '#1565C0', label: 'Government' },
              { color: '#E65100', label: 'Private' },
            ].map((item) => (
              <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: item.color,
                    border: '1px solid white',
                    boxShadow: '0 0 2px rgba(0,0,0,0.3)',
                  }}
                />
                <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                  {item.label}
                </Typography>
              </Box>
            ))}
          </Paper>
        )}

        {/* Data summary chip */}
        {!loading && dataMap.size > 0 && (
          <Chip
            icon={<LayersIcon sx={{ fontSize: 14 }} />}
            label={`${dataMap.size} ${level}s with data`}
            size="small"
            sx={{
              position: 'absolute',
              top: 55,
              left: 55,
              zIndex: 1000,
              fontSize: '0.68rem',
              height: 24,
              backgroundColor: 'rgba(255,255,255,0.9)',
            }}
          />
        )}
      </Box>

      {/* Sidebar */}
      <MapSidebar
        open={sidebarOpen}
        areaName={sidebarAreaName}
        areaLevel={level === 'region' ? 'Region' : level === 'district' ? 'District' : 'Ward'}
        data={sidebarData}
        onClose={() => setSidebarOpen(false)}
        childAreas={sidebarChildAreas}
        onDrillDown={drillToWard}
        canDrillDown={level === 'district' && !!selectedDistrictCode}
      />
    </Box>
  );
};

export default ZanzibarMap;
